import { createFileRoute, Link } from "@tanstack/react-router";
import { Breadcrumbs, breadcrumbJsonLd } from "@/components/Breadcrumbs";
import { GuiasRelacionadas } from "@/components/GuiasRelacionadas";
import hero1280Asset from "../assets/hero-1280.webp.asset.json";
import hero1920Asset from "../assets/hero-1920.webp.asset.json";
import hero640Asset from "../assets/hero-640.webp.asset.json";

const srcSet = `${hero640Asset.url} 640w, ${hero1280Asset.url} 1280w, ${hero1920Asset.url} 1920w`;
const SITE = "https://elpajardebenito.es";
const URL_CANON = `${SITE}/casa-rural-hayedo-tejera-negra`;
const OG_IMAGE = `${SITE}${hero1280Asset.url}`;

const faqs = [
  { q: "¿A qué distancia está la casa del Hayedo de Tejera Negra?", a: "El Ordial está a unos 50 minutos en coche del aparcamiento del Robledal, el acceso principal al hayedo. Es una de las opciones de alojamiento más cercanas dentro de la Sierra Norte de Guadalajara." },
  { q: "¿Hay que reservar para entrar al hayedo?", a: "Sí. En temporada alta (otoño y fines de semana) el acceso al aparcamiento del Robledal está regulado y hay que reservar plaza con antelación a través de la web oficial de la Junta de Comunidades de Castilla-La Mancha. Las plazas de los fines de semana de octubre se agotan en minutos." },
  { q: "¿Cuándo está el hayedo en su mejor momento?", a: "Las dos o tres últimas semanas de octubre y la primera de noviembre, cuando las hayas pasan del verde al ocre y al cobre. La fecha exacta varía cada año según cuándo lleguen las primeras heladas." },
  { q: "¿Qué ruta se hace dentro del hayedo?", a: "La más conocida es la Senda de Carretas, de dificultad baja y unas 2 h. La Senda del Robledal es más corta y accesible. Ambas parten de la zona del aparcamiento del Robledal." },
  { q: "¿Merece la pena ir fuera del otoño?", a: "Sí. En primavera el bosque está verde intenso y con agua en los arroyos, y hay muchísima menos gente y menos restricciones de acceso. En invierno, con nieve, el paisaje es otro." },
];

export const Route = createFileRoute("/casa-rural-hayedo-tejera-negra")({
  head: () => ({
    meta: [
      { title: "Casa rural Hayedo de Tejera Negra desde 55 € | El Pajar de Benito" },
      { name: "description", content: "Alojamiento para ver el otoño del Hayedo de Tejera Negra: habitaciones con baño privado desde 55 €/noche o casa completa con chimenea hasta 14 personas. Reserva directa." },
      { property: "og:title", content: "Casa rural Hayedo de Tejera Negra desde 55 €" },
      { property: "og:description", content: "Habitaciones con baño privado o casa completa con chimenea en la Sierra Norte de Guadalajara. Reserva directa sin comisiones." },
      { property: "og:url", content: URL_CANON },
      { property: "og:type", content: "website" },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: URL_CANON }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbJsonLd([
            { label: "Inicio", to: "/" },
            { label: "Casa rural cerca del Hayedo de Tejera Negra", to: "/casa-rural-hayedo-tejera-negra" },
          ]),
        ),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <Breadcrumbs items={[{ label: "Inicio", to: "/" }, { label: "Cerca del Hayedo de Tejera Negra" }]} />
      <section className="relative h-[55vh] min-h-[380px] w-full overflow-hidden">
        <img src={hero1280Asset.url} srcSet={srcSet} sizes="100vw" alt="Bosque de la Sierra Norte de Guadalajara en otoño" width={1920} height={1280} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 mx-auto flex h-full max-w-5xl flex-col justify-end px-4 pb-16 md:px-6">
          <p className="text-xs uppercase tracking-[0.28em] text-white/80">Otoño en la sierra</p>
          <h1 className="mt-3 font-serif text-4xl text-white md:text-5xl">Casa rural cerca del Hayedo de Tejera Negra</h1>
          <p className="mt-4 max-w-2xl text-white/90">A 50 minutos del aparcamiento del Robledal, en plena Sierra Norte de Guadalajara.</p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-16 md:px-6">
        <h2 className="font-serif text-3xl text-foreground">El otoño más famoso del centro de España</h2>
        <p className="mt-4 text-muted-foreground">
          El <strong>Hayedo de Tejera Negra</strong> es uno de los hayedos más meridionales de Europa, Parque Natural y parte de la Reserva de la Biosfera de la Sierra del Rincón. En octubre se convierte en el destino de otoño por excelencia del centro peninsular: por eso el acceso está regulado y las plazas de aparcamiento vuelan.
        </p>
        <p className="mt-4 text-muted-foreground">
          Alojarse en la comarca cambia por completo la experiencia. Desde <strong>El Pajar de Benito</strong>, en El Ordial, llegas al hayedo en unos 50 minutos, lo que te permite entrar a primera hora —la mejor luz y el bosque casi vacío— sin haber conducido antes dos horas desde Madrid.
        </p>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Antes de ir: lo que conviene saber</h2>
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          {[
            { t: "Reserva de plaza obligatoria", d: "En temporada alta hay que reservar el aparcamiento del Robledal en la web oficial de la JCCM. Sin reserva no se accede." },
            { t: "Senda de Carretas", d: "La ruta clásica: unas 2 h, dificultad baja, atraviesa el corazón del hayedo." },
            { t: "Primera hora", d: "La niebla del amanecer entre las hayas es la mejor postal, y hay mucha menos gente." },
            { t: "Calzado y capas", d: "El suelo está húmedo y hay bastante diferencia de temperatura entre la sombra del bosque y el claro." },
          ].map((b) => (
            <div key={b.t} className="rounded-sm border border-border bg-card p-6">
              <h3 className="font-serif text-lg">{b.t}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{b.d}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-sm text-muted-foreground">
          Tienes todos los detalles prácticos en nuestra{" "}
          <Link to="/blog/$slug" params={{ slug: "guia-hayedo-tejera-negra" }} className="font-medium text-primary hover:text-primary/80">
            guía completa del Hayedo de Tejera Negra
          </Link>.
        </p>
      </section>

      <section className="bg-muted/40">
        <div className="mx-auto max-w-4xl px-4 py-16 md:px-6">
          <h2 className="font-serif text-3xl text-foreground">Y si no consigues plaza, hay plan B</h2>
          <p className="mt-4 text-muted-foreground">
            El hayedo no es lo único que se pone precioso en otoño. Desde la casa tienes a mano la subida al Pico Alto Rey, los robledales de la Sierra del Alto Rey, la cascada de Despeñalagua en Valverde de los Arroyos y toda la ruta de los pueblos negros. Un fin de semana de otoño en la zona se llena sin esfuerzo.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-16 md:px-6">
        <h2 className="font-serif text-3xl text-foreground">Preguntas frecuentes</h2>
        <div className="mt-8 space-y-6">
          {faqs.map((f) => (
            <div key={f.q}>
              <h3 className="font-medium text-foreground">{f.q}</h3>
              <p className="mt-2 text-muted-foreground">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <GuiasRelacionadas slugs={["guia-hayedo-tejera-negra", "que-ver-sierra-norte-guadalajara", "subida-alto-rey"]} />

      <section className="mx-auto max-w-3xl px-4 py-24 text-center md:px-6">
        <h2 className="font-serif text-3xl text-foreground md:text-4xl">Reserva tus fechas de otoño</h2>
        <p className="mt-4 text-muted-foreground">Octubre se llena pronto. Comprueba disponibilidad en tiempo real.</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/reserva" className="rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90">Ver disponibilidad</Link>
          <Link to="/entorno" className="rounded-full border border-border px-6 py-3 text-sm font-medium hover:bg-muted">Qué ver cerca</Link>
        </div>
      </section>
    </>
  );
}
